/*
 * Keeps each "Pizza extras" line in step with the pizza it belongs to.
 *
 * The pizza builder (assets/bpp-builder.js) adds a pizza with property
 * _bpp_build and, when its options cost extra, a "Pizza extras" line with
 * _bpp_extras set to the same value and _bpp_per, its quantity for ONE pizza.
 * The cart hides that line's row and folds its price into the pizza's own
 * price (snippets/bpp-cart-line-price.liquid), so the customer cannot change it
 * directly. Taste's cart can still change the pizza (quantity, remove), and
 * then the extras must follow: two pizzas carry two pizzas' extras, a removed
 * pizza takes its extras with it.
 *
 * So after every cart change, and once on load when the cart holds extras, the
 * extras quantities are checked against their pizzas and corrected in one
 * /cart/update request that also redraws the cart. Nothing in Taste's own cart
 * code changes.
 */
(() => {
  if (window.bppBuilderCart) return;
  window.bppBuilderCart = true;

  /* The extras quantities the cart should have, as { lineKey: quantity }. */
  function corrections(cart) {
    const pizzas = {};
    (cart.items || []).forEach((item) => {
      const build = item.properties && item.properties._bpp_build;
      if (build) pizzas[build] = (pizzas[build] || 0) + item.quantity;
    });

    const updates = {};
    (cart.items || []).forEach((item) => {
      const build = item.properties && item.properties._bpp_extras;
      if (!build) return;
      const per = parseInt(item.properties._bpp_per, 10) || 1;
      const want = (pizzas[build] || 0) * per;
      if (item.quantity !== want) updates[item.key] = want;
    });
    return updates;
  }

  /* Every cart UI on the page: the drawer, and the cart page on /cart. */
  function sectionsToRender() {
    const sections = [];
    ['cart-drawer-items', 'cart-items'].forEach((tag) => {
      const host = document.querySelector(tag);
      if (!host || typeof host.getSectionsToRender !== 'function') return;
      host.getSectionsToRender().forEach((section) => {
        if (!sections.some((known) => known.id === section.id)) sections.push(section);
      });
    });
    return sections;
  }

  /* Same redraw as CartItems.updateQuantity() in assets/cart.js. */
  function render(sections, state) {
    const empty = state.item_count === 0;
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', empty);
    document.querySelector('cart-drawer-items')?.classList.toggle('is-empty', empty);
    document.querySelector('cart-items')?.classList.toggle('is-empty', empty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);

    sections.forEach((section) => {
      const html = state.sections && state.sections[section.section || section.id];
      const container = document.getElementById(section.id);
      if (!html || !container) return;
      const selector = section.selector || '.shopify-section';
      const target = container.querySelector(selector) || container;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (source) target.innerHTML = source.innerHTML;
    });
  }

  let running = false;
  let queued = false;

  async function sync(cart) {
    if (running) {
      queued = true;
      return;
    }
    running = true;
    try {
      const current = cart || (await fetch(`${window.routes.cart_url}.js`).then((response) => response.json()));
      const updates = corrections(current);
      if (!Object.keys(updates).length) return;

      const sections = sectionsToRender();
      const body = JSON.stringify({
        updates,
        sections: sections.map((section) => section.section || section.id),
        sections_url: window.location.pathname,
      });
      const response = await fetch(`${window.routes.cart_update_url}`, { ...fetchConfig(), body });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || state.message || response.status);

      render(sections, state);

      // Let HulkApps, the sticky cart and the rest recompute from the corrected
      // cart. 'cart-items' tells Taste's cart that the UI is already redrawn.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state, bppExtras: true });
      }
    } catch (error) {
      console.error('bpp-builder-cart: could not keep pizza extras in step', error);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        sync();
      }
    }
  }

  function cartHasExtras() {
    return Boolean(document.querySelector('[data-bpp-extras-row]'));
  }

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.bppExtras) return;
        const data = event && event.cartData;
        // A quantity change or removal hands over the whole cart; an add from
        // a product form hands over only the added item.
        if (data && Array.isArray(data.items)) sync(data);
        else if (cartHasExtras()) sync();
      });
    }
    // Catches a cart changed in another tab, or a sync cut short by leaving.
    if (cartHasExtras()) sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
